import R from 'ramda';
import _ from 'lodash';
import numeral from 'numeral';
import moment from 'moment';
import {
  COST_COSYND_ADMIN_FEE,
  COST_SINGLE_AGREEMENT,
  REFERRER_IDS,
} from '../constants';

export const getShortName = (name) => {
  const names = name.split(' ');
  const last = names[names.length - 1];
  names[names.length - 1] = last.charAt(0) + '.';

  return names.join(' ');
};

export const splitName = (name) => {
  const names = name.split(' ');
  const firstName = names[0];

  names.splice(0, 1);
  const lastName = names.join(' ');

  return {
    firstName,
    lastName,
  };
};

export const getRoleNames = (ids, roles) => {
  let roleNames = [];
  ids.forEach((id) => {
    const index = R.findIndex(R.propEq('id', parseInt(id, 10)))(roles);
    if (index !== -1) {
      roleNames.push(roles[index].title);
    }
  });

  return roleNames;
};

export const getOwnershipTypeName = (id, ownershipTypes) => {
  const index = R.findIndex(R.propEq('id', parseInt(id, 10)))(ownershipTypes);
  if (index !== -1) {
    return ownershipTypes[index].name;
  } else {
    return '';
  }
};

export const getOwnershipOptionByWorkType = (type, subtype, ownershipTypes) => {
  const ownershipTypeList = _.filter(
    ownershipTypes,
    (ownership) => ownership.work_type === parseInt(type, 10)
  );

  if (ownershipTypeList.length > 0) {
    if (parseInt(type, 10) === 2 && parseInt(subtype, 10) === 1) {
      // selected "Music && Sound Recording"
      return ownershipTypeList[1];
    }
    return ownershipTypeList[0];
  }

  return '';
};

export const getUserById = (id, users) => {
  const index = R.findIndex(R.propEq('id', parseInt(id, 10)))(users);
  if (index !== -1) {
    return users[index];
  } else {
    return null;
  }
};

export const getUserNameById = (id, users) => {
  const user = getUserById(id, users);
  if (user) {
    if (user.name.length === 0) {
      return user.email;
    } else {
      return user.name;
    }
  } else {
    return '';
  }
};

export const getMemberByUserId = (userId, members) => {
  const index = R.findIndex(R.propEq('user', parseInt(userId, 10)))(members);
  if (index !== -1) {
    return members[index];
  } else {
    return null;
  }
};

export const getMemberNameByUserId = (userId, members) => {
  const member = getMemberByUserId(userId, members);
  if (member && member.user_info) {
    return member.user_info.name;
  } else {
    return '';
  }
};

export const getUserNameByInviteId = (id, invites) => {
  const user = invites.find((invite) => invite.invitee === id);
  if (user) {
    if (user.name.length === 0) {
      return user.email;
    } else {
      return user.name;
    }
  } else {
    return '';
  }
};

export const getUserEmailById = (id, users) => {
  const user = getUserById(id, users);
  if (user) {
    return user.email;
  } else {
    return '';
  }
};

export const getWorkTypeNameById = (id, workTypes) => {
  const workType = R.find(R.propEq('id', parseInt(id, 10)))(workTypes);
  if (workType) {
    return workType.name;
  }
  return '';
};

export const getWorkTypeOption = (id, workTypes) => {
  const workType = R.find(R.propEq('id', parseInt(id, 10)))(workTypes);

  if (workType) {
    if (workType.name === 'Music') {
      return 'Music Composition';
    } else if (workType.name === 'Video') {
      return 'Entire Production';
    } else if (workType.name === 'Image') {
      return 'Entire Image';
    } else if (workType.name === 'Document') {
      return 'Entire Document';
    }
  }
  return 'Musical Composition';
};

export const getSubWorkTypeNameById = (id, subWorkTypes) => {
  const subWorkType = R.find(R.propEq('id', parseInt(id, 10)))(subWorkTypes);
  if (subWorkType) {
    return subWorkType.name;
  }
  return '';
};

export const populateContributions = (roleNames) => {
  const mappings = {
    Director: 'Direction',
    Producer: 'Production',
    Writer: 'Script/Screenplay',
    Editor: 'Editing',
    'Sound Recording Owner': 'Sound Recording',
    Arranger: 'Musical Arrangement',
    Photographer: 'Photograph',
    Illustrator: 'Artwork',
  };

  return roleNames.map((roleName) => {
    const index = Object.keys(mappings).indexOf(roleName);

    if (index === -1) {
      return 'Other';
    } else {
      return Object.values(mappings)[index];
    }
  });
};

export const getAddress = ({ address, city, state, zip, country }) => {
  let line = '';

  if (address) {
    line = address + ',';
  }
  if (city) {
    line = line + ' ' + city + ',';
  }
  if (state) {
    line = line + ' ' + state;
  }
  if (zip) {
    line = line + ' ' + zip;
  }
  if (country) {
    line = line + ' ' + country;
  }

  return line;
};

export const getFullName = ({ firstName, middleName, lastName }) => {
  let names = [];

  if (firstName) {
    names.push(firstName);
  }
  if (middleName) {
    names.push(middleName);
  }
  if (lastName) {
    names.push(lastName);
  }

  return names.join(' ');
};

export const compareJSONObjects = (obj1, obj2) => {
  if (!obj1 || !obj2) {
    return !obj1 && !obj2;
  } else if (obj1 === obj2) {
    return true;
  } else if (JSON.stringify(obj1) === JSON.stringify(obj2)) {
    return true;
  } else if (Object.keys(obj1).length !== Object.keys(obj2).length) {
    return false;
  } else {
    for (let key in obj1) {
      if (obj1.hasOwnProperty(key) !== obj2.hasOwnProperty(key)) {
        return false;
      } else if (typeof obj1[key] !== typeof obj2[key]) {
        return false;
      }

      switch (typeof obj1[key]) {
        case 'object':
        case 'function':
          if (!compareJSONObjects(obj1[key], obj2[key])) {
            return false;
          }
          break;

        default:
          if (obj1[key] !== obj2[key]) {
            return false;
          }
          break;
      }
    }

    return true;
  }
};

export const floatFormat = (str) => {
  return numeral(str).format('0.00');
};

export const getRoleIds = (options) => {
  return options.map((option) => parseInt(option.value, 10));
};

export const getStringFromArray = (type, options) => {
  let contentArray = [];
  let joinContent = '';

  if (type === 'services') {
    if (options.engineering === true) {
      contentArray = [...contentArray, 'engineering'];
    }
    if (options.mixing === true) {
      contentArray = [...contentArray, 'mixing'];
    }
    if (options.production === true) {
      contentArray = [...contentArray, 'production'];
    }
  } else if (type === 'payments') {
    if (options.sales) {
      contentArray = [...contentArray, 'sales'];
    }
    if (options.performances) {
      contentArray = [...contentArray, 'performances'];
    }
    if (options.licenses) {
      contentArray = [...contentArray, 'licenses'];
    }
    if (options.any) {
      contentArray = [...contentArray, 'any other exploitations'];
    }
  }

  // Join contentArray
  const last = _.last(contentArray);
  const subArray = _.slice(contentArray, 0, contentArray.length - 1);
  if (contentArray.length === 0) {
    joinContent = '';
  } else if (contentArray.length === 1) {
    joinContent = last;
  } else {
    joinContent = last ? subArray.join(', ') + ' and ' + last : '';
  }
  return joinContent;
};

export const getSubscriptionType = (subscriptionPlan, subscription) => {
  if (subscriptionPlan) {
    if (
      subscription &&
      subscription.status === 'Canceled' &&
      subscriptionPlan === 'free'
    ) {
      return {
        planType: 'free',
      };
    }

    if (subscriptionPlan.includes('-')) {
      return {
        planType: subscriptionPlan.split('-')[0],
        planDuration: subscriptionPlan.split('-')[1],
        cdbabyPlan: subscriptionPlan.split('-')[2] || '',
      };
    }
    return {
      planType: subscriptionPlan.split('_')[0],
      planDuration: subscriptionPlan.split('_')[1],
      cdbabyPlan: subscriptionPlan.split('-')[2] || '',
    };
  } else {
    return {
      planType: 'free',
    };
  }
};

export const getSubscriptionTypeForCreg = (subscriptionPlan, subscription) => {
  if (subscriptionPlan) {
    if (
      subscription &&
      subscription.status === 'Canceled' &&
      subscriptionPlan === 'free'
    ) {
      return {
        planType: 'free',
      };
    }

    if (subscriptionPlan.includes('-')) {
      return {
        planType: subscriptionPlan.split('-')[0],
        planDuration: subscriptionPlan.split('-')[1],
      };
    } else if (subscriptionPlan.includes('_')) {
      return {
        planType: subscriptionPlan.split('_')[0],
        planDuration: subscriptionPlan.split('_')[1],
      };
    }
  }

  return {
    planType: 'free',
  };
};

export const getCurrentPlan = (
  profile
) => {

  switch (profile.subscription_plan) {
    case 'tier_1m':
      return 'Starter - $195/month';

    case 'tier_2m':
      return 'Growth - $500/month';

    case 'tier_3m':
      return 'Enterprise';

    case 'tier_1a':
      return 'Starter - $2.140';

    case 'tier_2a':
      return 'Growth - $5.000';

    case 'tier_3a':
      return 'Enterprise - $25.000';

    default:
      return 'Free - $0';
  }

  // if (!subscription) {
  //   if (
  //     profile.subscription_plan === 'premium-annual' ||
  //     profile.subscription_plan === 'premium_annual'
  //   ) {
  //     return 'Premium - $150/yr';
  //   }

  //   if (profile.subscription_plan === 'premium-annual-cdbaby') {
  //     return 'Premium - $150/yr';
  //   }
  // }

  // if (
  //   subscription &&
  //   subscription.status === 'Canceled' &&
  //   subscriptionPlan === 'free'
  // ) {
  //   return 'Free - $0';
  // }

  // if (subscriptionPlan) {
  //   if (subscriptionPlan === 'premium') {
  //     if (
  //       profile.referrer.includes('cdbaby') ||
  //       profile.referrer.includes('soundrop')
  //     ) {
  //       return 'Free - $0';
  //     } else {
  //       if (trialExpired) {
  //         return 'Premium - 30days trial expired';
  //       } else {
  //         return 'Premium - 30days trial';
  //       }
  //     }
  //   } else {
  //     const { planType, planDuration, cdbabyPlan } = getSubscriptionType(
  //       subscriptionPlan,
  //       subscription
  //     );

  //     if (cdbabyPlan !== '') {
  //       return 'Premium - $150/yr';
  //     }

  //     if (planType === 'premium') {
  //       if (planDuration === 'annual') {
  //         currentPlan = 'Premium - $150/yr';
  //       } else {
  //         currentPlan = 'Premium - $15/mo';
  //       }
  //     } else if (planType === 'pro') {
  //       currentPlan = subscriptionPlan;
  //     } else if (planType === 'essential') {
  //       currentPlan = 'Essential';
  //     }
  //   }
  // }
};

export const getCregDiscountedAdminFee = (referrerId /*planType*/) => {
  // if (referrerId && planType !== 'premium') {
  if (referrerId) {
    if (referrerId.includes('cdbaby') || referrerId.includes('soundrop')) {
      return COST_COSYND_ADMIN_FEE - 10;
    } else if (referrerId.includes('partnera2imartistpro') || referrerId.includes('diymg')) {
      return COST_COSYND_ADMIN_FEE - 15;
    } else if (referrerId.includes('xposure')) {
      return COST_COSYND_ADMIN_FEE;
    } else if (
      _.findIndex(
        REFERRER_IDS,
        (id) => id.toLowerCase() === referrerId.toLowerCase()
      ) !== -1
    ) {
      return COST_COSYND_ADMIN_FEE - 10;
    } else {
      return COST_COSYND_ADMIN_FEE;
    }
  }
  return COST_COSYND_ADMIN_FEE;
};

export const getAgreementDiscountedCost = (referrerId) => {
  if (referrerId) {
    if (referrerId.includes('cdbaby') || referrerId.includes('soundrop')) {
      return COST_SINGLE_AGREEMENT - 5;
    } else if (referrerId.includes('xposure')) {
      return COST_SINGLE_AGREEMENT;
    } else if (
      _.findIndex(
        REFERRER_IDS,
        (id) => id.toLowerCase() === referrerId.toLowerCase()
      ) !== -1
    ) {
      return COST_SINGLE_AGREEMENT - 5;
    } else {
      return COST_SINGLE_AGREEMENT;
    }
  }
  return COST_SINGLE_AGREEMENT;
};

export const getSubscriptionExpiredStatus = (subscription) => {
  // Return subscription expires date for essential and premium-monthly users to upgrade the subscription
  if (subscription) {
    const today = moment(subscription.today).format('YYYY-MM-DD');
    const subscriptionExpiresDate = moment(subscription.next_billing_date)
      .subtract(1, 'day')
      .format('YYYY-MM-DD');

    if (
      today === subscriptionExpiresDate &&
      subscription.status !== 'Canceled'
    ) {
      if (
        subscription.plan_id !== 'premium-annual' &&
        subscription.plan_id !== 'premium_annual' &&
        subscription.plan_id !== 'premium-annual-cdbaby' &&
        subscription.plan_id !== 'pro-annual'
      ) {
        return true;
      }
    }
  }
  return false;
};

export const getUnMatchedClaimants = (authors, claimants, type) => {
  let result = [];
  let count = 0;
  for (let i = 0; i < claimants.length; i++) {
    const index = _.findIndex(
      authors,
      (author) =>
        author.firstName === claimants[i].firstName &&
        author.lastName === claimants[i].lastName &&
        author.organization === claimants[i].organization
    );
    if (index !== -1) {
      count += 1;
    }
  }

  const transferredClaimants = _.filter(claimants, (claimant) =>
    _.has(claimant, 'transfer')
  );
  if (count === claimants.length) {
    if (count === authors.length) {
      if (type) {
        result = [...transferredClaimants];
      } else {
        result = [];
      }
    } else {
      result = _.filter(
        authors,
        (author) =>
          _.findIndex(
            claimants,
            (claimant) =>
              claimant.firstName === author.firstName &&
              claimant.lastName === author.lastName &&
              claimant.organization === author.organization
          ) === -1
      );
      if (type) {
        for (let i = 0; i < transferredClaimants.length; i++) {
          const index = _.findIndex(
            result,
            (r) => r.id === transferredClaimants[i].id
          );
          if (index === -1) {
            result = [...result, transferredClaimants[i]];
          }
        }
      }
    }
  } else {
    result = _.filter(
      claimants,
      (claimant) =>
        _.findIndex(
          authors,
          (author) =>
            author.firstName === claimant.firstName &&
            author.lastName === claimant.lastName &&
            author.organization === claimant.organization
        ) === -1
    );
    if (type) {
      for (let i = 0; i < transferredClaimants.length; i++) {
        const index = _.findIndex(
          result,
          (r) => r.id === transferredClaimants[i].id
        );
        if (index === -1) {
          result = [...result, transferredClaimants[i]];
        }
      }
    }
  }
  return result;
};

export const commpareObjectArray = (origins, changes) => {
  let count = 0;
  if (origins.length === changes.length) {
    origins.forEach((origin) => {
      if (_.findIndex(changes, (change) => _.isEqual(change, origin)) !== -1) {
        count++;
      }
    });
    return count === origins.length;
  } else {
    return false;
  }
};

export const getUserDealChange = (dealChanges, blockId, userId, isOwner) => {
  if (dealChanges.length === 0) return null;

  const userDealChanges = _.filter(dealChanges, (data) => {
    if (data.user === userId) {
      const changes = JSON.parse(data.changes);
      if (changes.contract_block && changes.contract_block.id === blockId) {
        if (isOwner) {
          if (data.type === 'update-contract-block') {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      }
    }
    return false;
  });

  return userDealChanges.length > 0 ? userDealChanges[0] : null;
};

export const compareWithOwnerDealChange = (
  ownerDealChange,
  userDealChange,
  accepts,
  accepts2,
  userId
) => {
  if (ownerDealChange) {
    if (!compareJSONObjects(ownerDealChange.changes, userDealChange.changes)) {
      if (userDealChange.accept) {
        accepts =
          accepts.length > 0
            ? _.filter(accepts, (accept) => accept.user.id !== userId)
            : [];
      } else if (userDealChange.accept2) {
        accepts2 =
          accepts2.length > 0
            ? _.filter(accepts2, (accept) => accept.user.id !== userId)
            : [];
      }
    }
  }
  return { accepts, accepts2 };
};



// WEBPACK FOOTER //
// src/utils/index.js